import React, { useState } from "react";
import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalBody } from "../../components/modalCommon/modalBody";
import { ModalFooter } from "../../components/modalCommon/modalFooter";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import { useToast } from "../../components/toast/toast";
import { getApiUrl } from "../../utils/getApiUrl";
import { fetchWithAuth, getUser, isAuthenticated } from "../../auth/auth";
import { sendIcon } from "../../utils/config";
import type { ShareOpinionModalModel } from "../../type/modal";

const MAX_LENGTH = 2000;

const ShareOpinionModal: React.FC<ShareOpinionModalModel> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [text, setText] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const isGuest = !isAuthenticated();

  const handleClose = () => {
    setText("");
    setEmail("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim().length === 0) return;

    setLoading(true);
    try {
      const user = isAuthenticated() ? getUser() : null;
      const res = await fetchWithAuth(getApiUrl("/messages/add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          iduser: user?.iduser ?? null,
          email: user ? user.email : email.trim(),
          text: text.trim(),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
      }

      showToast(t("Dziękujemy za opinię!"), "info");
      handleClose();
    } catch (err: unknown) {
      showToast(
        err instanceof Error ? err.message : t("Wystąpił błąd. Spróbuj ponownie."),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal open={isOpen} onClose={handleClose} size="md">
      <ModalHeader title={t("Share your opinion")} onClose={handleClose} />
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <p className="mb-3 text-secondary">
            {t("We are constantly improving our application. Share your opinion on what is available or what you like most.")}
          </p>
          {isGuest && (
            <div className="mb-3">
              <label htmlFor="share-opinion-email" className="form-label">
                {t("Adres e-mail")} <span className="text-danger">*</span>
              </label>
              <input
                id="share-opinion-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("Adres e-mail")}
                disabled={loading}
                required
                autoFocus
              />
            </div>
          )}
          <div className="mb-1">
            <label htmlFor="share-opinion-text" className="form-label">
              {t("Your message")}
            </label>
            <textarea
              id="share-opinion-text"
              className="form-control"
              rows={6}
              maxLength={MAX_LENGTH}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("Write your feedback here…")}
              disabled={loading}
              autoFocus={!isGuest}
            />
            <small className="d-block text-end mt-1">
              {text.length} / {MAX_LENGTH}
            </small>
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t("Cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || text.trim().length === 0 || (isGuest && email.trim().length === 0)}
          >
            {loading ? t("Sending…") : <>{sendIcon} {t("Send")}</>}
          </button>
        </ModalFooter>
      </form>
    </BaseModal>
  );
};

export default ShareOpinionModal;
